import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function ConsumableDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [consumable, setConsumable] = useState({
    product_name: '',
    product_code: '',
    type: 'Consumable',
    product_category: '',
    product_description: '',
    customer: '',
    cost_price: '',
    markup: '',
    price: '',
    sales_tax_rate: '20% VAT',
    nominal_code: '',
    location: '',
    barcode: '',
    supplier_id: '',
    supplier_sku: '',
    reorder_level: 0,
    reorder_quantity: 0,
    current_stock: 0,
    photo_url: '',
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchConsumable();
    }
  }, [id]);

  const fetchConsumable = async () => {
    try {
      const response = await apiFetch(`/api/products/${id}`);
      const data = await response.json();
      setConsumable(data);
      if (data.photo_url) {
        setPhotoPreview(data.photo_url);
      }
    } catch (error) {
      console.error('Error fetching consumable:', error);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveConsumable = async () => {
    if (!consumable.product_name || !consumable.product_code || !consumable.price) {
      alert('Product Name, Product Code, and Price are required');
      return;
    }

    setSaving(true);
    try {
      const consumableData = {
        ...consumable,
        type: 'Consumable',
        cost_price: parseFloat(consumable.cost_price || 0),
        markup: parseFloat(consumable.markup || 0),
        price: parseFloat(consumable.price || 0),
        reorder_level: parseInt(consumable.reorder_level || 0),
        reorder_quantity: parseInt(consumable.reorder_quantity || 0),
        current_stock: parseFloat(consumable.current_stock || 0),
      };

      if (photoFile) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          consumableData.photo_url = reader.result;
          await submitConsumable(consumableData);
        };
        reader.readAsDataURL(photoFile);
      } else {
        await submitConsumable(consumableData);
      }
    } catch (error) {
      console.error('Error saving consumable:', error);
      alert('Failed to save consumable');
      setSaving(false);
    }
  };

  const submitConsumable = async (data) => {
    try {
      const url = isNew 
        ? '/api/products'
        : `/api/products/${id}`;
      
      const method = isNew ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        alert(isNew ? 'Consumable created!' : 'Consumable updated!');
        navigate('/consumables');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error submitting consumable:', error);
      alert('Failed to save consumable');
    } finally {
      setSaving(false);
    }
  };

  const deleteConsumable = async () => {
    if (!window.confirm('Are you sure you want to delete this consumable?')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await apiFetch(`/api/products/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Consumable deleted!');
        navigate('/consumables');
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting consumable:', error);
      alert('Failed to delete consumable');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/consumables')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">
                {isNew ? 'Add Consumable' : consumable.product_name}
              </h1>
              {consumable.product_code && (
                <p className="text-sm text-blue-100 mt-1">Code: {consumable.product_code}</p>
              )}
            </div>
            <div className="flex gap-2">
              {!isNew && (
                <button
                  onClick={deleteConsumable}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
                >
                  {deleting ? '⏳ Deleting...' : '🗑️ Delete'}
                </button>
              )}
              <button
                onClick={saveConsumable}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8">

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Product Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Consumable Name *
            </label>
            <input
              type="text"
              value={consumable.product_name}
              onChange={(e) => setConsumable({ ...consumable, product_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Safety Gloves - Large"
            />
          </div>

          {/* Product Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Code *
            </label>
            <input
              type="text"
              value={consumable.product_code}
              onChange={(e) => setConsumable({ ...consumable, product_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., GLOVE-L"
            />
          </div>

          {/* Barcode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Barcode
            </label>
            <input
              type="text"
              value={consumable.barcode}
              onChange={(e) => setConsumable({ ...consumable, barcode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Scan or enter barcode"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <input
              type="text"
              value={consumable.product_category}
              onChange={(e) => setConsumable({ ...consumable, product_category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Safety Equipment"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              value={consumable.location}
              onChange={(e) => setConsumable({ ...consumable, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Warehouse A - Shelf 3"
            />
          </div>

          {/* Cost Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cost Price (£)
            </label>
            <input
              type="number"
              step="0.01"
              value={consumable.cost_price}
              onChange={(e) => setConsumable({ ...consumable, cost_price: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>

          {/* Sell Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sell Price (£) *
            </label>
            <input
              type="number"
              step="0.01"
              value={consumable.price}
              onChange={(e) => setConsumable({ ...consumable, price: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>

          {/* Markup */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Markup (%)
            </label>
            <input
              type="number"
              step="0.1"
              value={consumable.markup}
              onChange={(e) => setConsumable({ ...consumable, markup: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0"
            />
          </div>

          {/* Current Stock */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Stock
            </label>
            <input
              type="number"
              step="1"
              value={consumable.current_stock}
              onChange={(e) => setConsumable({ ...consumable, current_stock: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0"
            />
          </div>

          {/* Reorder Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reorder Level
            </label>
            <input
              type="number"
              step="1"
              value={consumable.reorder_level}
              onChange={(e) => setConsumable({ ...consumable, reorder_level: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="10"
            />
          </div>

          {/* Reorder Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reorder Quantity
            </label>
            <input
              type="number"
              step="1"
              value={consumable.reorder_quantity}
              onChange={(e) => setConsumable({ ...consumable, reorder_quantity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="50"
            />
          </div>

          {/* Supplier SKU */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supplier SKU
            </label>
            <input
              type="text"
              value={consumable.supplier_sku}
              onChange={(e) => setConsumable({ ...consumable, supplier_sku: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Supplier's product code"
            />
          </div>

          {/* VAT Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              VAT Rate
            </label>
            <select
              value={consumable.sales_tax_rate}
              onChange={(e) => setConsumable({ ...consumable, sales_tax_rate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="20% VAT">20% VAT</option>
              <option value="0% VAT">0% VAT (Zero-rated)</option>
              <option value="Exempt">VAT Exempt</option>
            </select>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={consumable.product_description}
              onChange={(e) => setConsumable({ ...consumable, product_description: e.target.value })}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Detailed description..."
            />
          </div>

          {/* Photo Upload */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Photo
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {photoPreview && (
                <img src={photoPreview} alt="Preview" className="h-20 w-20 object-cover rounded-lg border" />
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
    </div>
  );
}

export default ConsumableDetail;
